import './AddEmployee.scss';

import * as formik from 'formik';
import * as yup from 'yup';

import { Alert, Form } from 'react-bootstrap';
import { compressImage, displayName } from '../../../commons/util/HallAppUtil';
import { useEffect, useMemo, useState } from 'react';

import AppButton from '../../../commons/buttons/AppButton';
import AppContainer from '../../../commons/containers/AppContainer';
import AppInput from '../../../commons/inputs/AppInput';
import AppLinkButton from '../../../commons/buttons/AppLinkButton';
import AppLoading from '../../../commons/AppLoading';
import CompanyAPI from '../../company/CompanyAPI';
import HallAppConst from '../../../commons/util/HallAppConst';
import HallAppToast from '../../../commons/HallAppToast';
import ServiceAPI from '../ServiceAPI';

function AddEmployee({ service, backStep, nextStep, serviceToEdit }) {
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  const companyAPI = new CompanyAPI();
  const serviceAPI = new ServiceAPI();

  // Monta valores iniciais (checkbox + percentage por funcionário)
  const buildInitialValues = (list, editing) => {
    const base = list.reduce((acc, e) => {
      acc[`checkbox_${e.id}`] = false;
      acc[`percentage_${e.id}`] = '';
      return acc;
    }, {});
    if (editing && serviceToEdit?.employees?.length) {
      for (const it of serviceToEdit.employees) {
        base[`checkbox_${it.employeeId}`] = it.percentage >= 0;
        base[`percentage_${it.employeeId}`] = it.percentage ?? '';
      }
    }
    return base;
  };

  const isEditing = useMemo(
    () => serviceToEdit != null && Object.keys(serviceToEdit).length > 0,
    [serviceToEdit]
  );

  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    getAllEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInitialValues(buildInitialValues(employees, isEditing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, isEditing]);

  const getAllEmployee = async () => {
    setIsLoading(true);
    try {
      const response = await companyAPI.getAllEmployee(companyAPI.getCompanyManagment().id);
      setEmployees(response.data || []);
    } catch (_) {
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  const { Formik } = formik;
  const schema = yup.object().shape(
    employees.reduce((acc, e) => {
      acc[`percentage_${e.id}`] = yup
        .number()
        .typeError('Informe um número válido')
        .min(0, 'Não pode ser menor que 0')
        .max(100, 'Não pode ser maior que 100')
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value));
      return acc;
    }, {})
  );

  const createOrUpdate = async (values) => {
    setIsLoading(true);

    try {
      const selectedEmployes = [];
      employees.forEach((e) => {
        const checked = values[`checkbox_${e.id}`];
        if (checked) {
          const percentage = values[`percentage_${e.id}`];
          selectedEmployes.push({
            employeeId: String(e.id),
            percentage: percentage === '' || percentage == null ? 0 : Number(percentage),
          });
        }
      });

      const data = {
        ...service,
        employees: selectedEmployes,
        companyId: companyAPI.getCompanyManagment().id,
      };

      const formData = new FormData();
      formData.append('dto', new Blob([JSON.stringify(data)], { type: 'application/json' }));

      if (service.img != null) {
        const logoCompress = await compressImage(service.img);
        formData.append('imgService', logoCompress);
      }

      if (isEditing) {
        await serviceAPI.update(serviceToEdit.id, formData);
      } else {
        await serviceAPI.create(formData);
      }

      HallAppToast.success();
      if (nextStep) nextStep(data);
    } catch (_) {
      HallAppToast.error(HallAppConst.GENERIC_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppContainer>
      <div className="add-employee">
        <Alert key="info" variant="info" className="add-employee__hint">
          Selecione os profissionais que executam este serviço. Opcionalmente, informe a porcentagem dos
          ganhos que será aplicada nas comandas. Você pode deixar em branco para não aplicar.
        </Alert>

        <h5 className="add-employee__title">Profissionais do serviço</h5>

        {isLoading ? (
          <AppLoading />
        ) : employees.length === 0 ? (
          <p className="add-employee__empty">
            Nenhum funcionário encontrado. Cadastre funcionários na área da empresa.
          </p>
        ) : (
          <Formik
            validationSchema={schema}
            onSubmit={createOrUpdate}
            enableReinitialize
            initialValues={initialValues}
          >
            {({ handleSubmit, handleChange, values, touched, errors, setFieldValue }) => (
              <Form noValidate onSubmit={handleSubmit} className="add-employee__form">
                <div className="add-employee__grid">
                  {employees.map((e) => {
                    const checked = !!values[`checkbox_${e.id}`];
                    const inputName = `percentage_${e.id}`;
                    const errorMsg = touched[inputName] && errors[inputName];

                    const toggleChecked = () => {
                      const isChecked = !checked;
                      setFieldValue(`checkbox_${e.id}`, isChecked);
                      if (!isChecked) setFieldValue(inputName, '');
                    };

                    return (
                      <div
                        key={e.id}
                        className={`employee-card ${checked ? 'is-active' : ''}`}
                        onClick={toggleChecked}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            toggleChecked();
                          }
                        }}
                        aria-pressed={checked}
                        aria-label={`Selecionar ${displayName(e?.name) || e?.email}`}
                      >
                        <div className="employee-card__row">
                          <div className="employee-card__left">
                            <input
                              className="form-check-input employee-card__checkbox"
                              type="checkbox"
                              onChange={(ev) => {
                                ev.stopPropagation();
                                toggleChecked();
                              }}
                              checked={checked}
                              aria-label="Selecionar profissional"
                            />
                            <span className="employee-card__name">
                              {displayName(e?.name) || e?.email}
                            </span>
                          </div>

                          <div
                            className="employee-card__right"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <div className="employee-card__pct">
                              <AppInput
                                name={inputName}
                                type="number"
                                min={0}
                                max={100}
                                step="0.1"
                                onChange={handleChange}
                                value={values[inputName]}
                                errors={errors}
                                touched={touched}
                                disabled={!checked}
                                label="Porcentagem"
                                placeholder="Ex.: 40"
                              />
                              {errorMsg ? (
                                <div className="field-error">{errorMsg}</div>
                              ) : (
                                <div className="field-hint">Opcional</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="add-employee__actions">
                  <AppButton width="100%" type="submit" name="Salvar" size="lg" disabled={isLoading} loading={isLoading} />
                  <AppLinkButton onClick={backStep} width="100%" name="Voltar" disabled={isLoading} />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </AppContainer>
  );
}

export default AddEmployee;